import React, { useMemo, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart } from 'recharts';
interface BurndownChartArtifactProps {
  data: {
    sprint: {
      name: string;
      startDate: string;
      endDate: string;
    } | null;
    issues: any[];
  };
}
function getStatusName(issue: any): string {
  const fields = issue.fields || issue;
  return (fields?.status?.name || fields?.status || issue.status?.name || issue.status || 'Unknown').toString();
}
function isDone(status: string): boolean {
  const lower = status.toLowerCase();
  return lower === 'done' || lower === 'closed' || lower === 'resolved';
}
function formatDate(d: Date): string {
  return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}`;
}
function getDaysBetween(start: Date, end: Date): number {
  return Math.max(1, Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)));
}
export function BurndownChartArtifact({
  data
}: BurndownChartArtifactProps) {
  const [tab, setTab] = useState<'burndown' | 'burnup'>('burndown');
  const {
    chartData,
    total,
    done,
    remaining,
    pctComplete
  } = useMemo(() => {
    const issues = Array.isArray(data.issues) ? data.issues : [];
    const total = issues.length;
    const done = issues.filter(i => isDone(getStatusName(i))).length;
    const remaining = total - done;
    // Determine sprint dates
    let startDate: Date;
    let endDate: Date;
    if (data.sprint?.startDate && data.sprint?.endDate) {
      startDate = new Date(data.sprint.startDate);
      endDate = new Date(data.sprint.endDate);
    } else {
      // Fallback: assume 14-day sprint ending today
      endDate = new Date();
      startDate = new Date(endDate.getTime() - 14 * 24 * 60 * 60 * 1000);
    }
    const totalDays = getDaysBetween(startDate, endDate);
    const now = new Date();
    const elapsedDays = Math.min(totalDays, Math.max(0, getDaysBetween(startDate, now)));
    // Build daily data points
    const points: {
      day: string;
      ideal: number;
      actual: number;
      done: number;
      scope: number;
    }[] = [];
    for (let d = 0; d <= totalDays; d++) {
      const date = new Date(startDate.getTime() + d * 24 * 60 * 60 * 1000);
      const label = formatDate(date);
      // Ideal: linear from total to 0
      const ideal = Math.max(0, Math.round(total - total / totalDays * d));
      // Actual: simulate based on done ratio
      let actualRemaining: number;
      let actualDone: number;
      if (d <= elapsedDays) {
        // Progress up to elapsed days — use an S-curve approximation
        const progress = d / Math.max(1, elapsedDays);
        const sCurve = progress < 0.5 ? 2 * progress * progress : 1 - Math.pow(-2 * progress + 2, 2) / 2;
        actualDone = Math.round(done * sCurve);
        actualRemaining = total - actualDone;
      } else {
        // Future: no data yet
        actualRemaining = NaN;
        actualDone = NaN;
      }
      points.push({
        day: label,
        ideal,
        actual: actualRemaining,
        done: actualDone,
        scope: total
      });
    }
    return {
      chartData: points,
      total,
      done,
      remaining,
      pctComplete: total > 0 ? Math.round(done / total * 100) : 0
    };
  }, [data]);
  if (!data.issues || data.issues.length === 0) {
    return <div className="p-6 text-center text-slate-500 text-sm" data-id="element-86">
        No hay issues para generar el burndown chart.
      </div>;
  }
  const CustomTooltip = ({
    active,
    payload,
    label
  }: any) => {
    if (!active || !payload?.length) return null;
    return <div className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 shadow-lg text-xs" data-id="element-87">
        <p className="text-slate-300 font-medium mb-1" data-id="element-88">{label}</p>
        {payload.map((entry: any, i: number) => <p key={i} style={{
        color: entry.color
      }} className="flex items-center gap-1.5" data-id="element-89">
            <span className="w-2 h-2 rounded-full" style={{
          backgroundColor: entry.color
        }} data-id="element-90" />
            {entry.name}: {isNaN(entry.value) ? '—' : entry.value}
          </p>)}
      </div>;
  };
  return <div className="p-4" data-id="element-91">
      {/* Tabs */}
      <div className="flex gap-1 mb-4 bg-slate-800/50 rounded-lg p-1 w-fit" data-id="element-92">
        <button onClick={() => setTab('burndown')} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${tab === 'burndown' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-400 hover:text-slate-200'}`} data-id="element-93">
          Burndown
        </button>
        <button onClick={() => setTab('burnup')} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${tab === 'burnup' ? 'bg-emerald-600 text-white shadow-sm' : 'text-slate-400 hover:text-slate-200'}`} data-id="element-94">
          Burnup
        </button>
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={300} data-id="element-95">
        {tab === 'burndown' ? <AreaChart data={chartData} margin={{
        top: 5,
        right: 20,
        left: 0,
        bottom: 5
      }} data-id="element-96">
            <defs data-id="element-97">
              <linearGradient id="burndownFill" x1="0" y1="0" x2="0" y2="1" data-id="element-98">
                <stop offset="5%" stopColor="#818cf8" stopOpacity={0.2} data-id="element-99" />
                <stop offset="95%" stopColor="#818cf8" stopOpacity={0} data-id="element-100" />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" data-id="element-101" />
            <XAxis dataKey="day" tick={{
          fill: '#64748b',
          fontSize: 11
        }} axisLine={{
          stroke: '#334155'
        }} tickLine={false} data-id="element-102" />
            <YAxis tick={{
          fill: '#64748b',
          fontSize: 11
        }} axisLine={{
          stroke: '#334155'
        }} tickLine={false} data-id="element-103" />
            <Tooltip content={<CustomTooltip data-id="element-105" />} data-id="element-104" />
            <Legend wrapperStyle={{
          fontSize: 11,
          color: '#94a3b8'
        }} data-id="element-106" />
            <Line type="linear" dataKey="ideal" name="Ideal" stroke="#64748b" strokeDasharray="6 3" strokeWidth={2} dot={false} connectNulls={false} data-id="element-107" />
            <Area type="monotone" dataKey="actual" name="Restante" stroke="#818cf8" strokeWidth={2} fill="url(#burndownFill)" dot={{
          fill: '#818cf8',
          r: 2,
          strokeWidth: 0
        }} activeDot={{
          r: 4,
          fill: '#818cf8'
        }} connectNulls={false} data-id="element-108" />
          </AreaChart> : <AreaChart data={chartData} margin={{
        top: 5,
        right: 20,
        left: 0,
        bottom: 5
      }} data-id="element-109">
            <defs data-id="element-110">
              <linearGradient id="burnupFill" x1="0" y1="0" x2="0" y2="1" data-id="element-111">
                <stop offset="5%" stopColor="#34d399" stopOpacity={0.2} data-id="element-112" />
                <stop offset="95%" stopColor="#34d399" stopOpacity={0} data-id="element-113" />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" data-id="element-114" />
            <XAxis dataKey="day" tick={{
          fill: '#64748b',
          fontSize: 11
        }} axisLine={{
          stroke: '#334155'
        }} tickLine={false} data-id="element-115" />
            <YAxis tick={{
          fill: '#64748b',
          fontSize: 11
        }} axisLine={{
          stroke: '#334155'
        }} tickLine={false} data-id="element-116" />
            <Tooltip content={<CustomTooltip data-id="element-118" />} data-id="element-117" />
            <Legend wrapperStyle={{
          fontSize: 11,
          color: '#94a3b8'
        }} data-id="element-119" />
            <Line type="linear" dataKey="scope" name="Alcance" stroke="#64748b" strokeDasharray="6 3" strokeWidth={2} dot={false} data-id="element-120" />
            <Area type="monotone" dataKey="done" name="Completado" stroke="#34d399" strokeWidth={2} fill="url(#burnupFill)" dot={{
          fill: '#34d399',
          r: 2,
          strokeWidth: 0
        }} activeDot={{
          r: 4,
          fill: '#34d399'
        }} connectNulls={false} data-id="element-121" />
          </AreaChart>}
      </ResponsiveContainer>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3 mt-4" data-id="element-122">
        <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50" data-id="element-123">
          <p className="text-[10px] text-slate-500 uppercase tracking-wider" data-id="element-124">
            Total
          </p>
          <p className="text-lg font-bold text-slate-200" data-id="element-125">{total}</p>
        </div>
        <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50" data-id="element-126">
          <p className="text-[10px] text-slate-500 uppercase tracking-wider" data-id="element-127">
            Completados
          </p>
          <p className="text-lg font-bold text-emerald-400" data-id="element-128">{done}</p>
        </div>
        <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50" data-id="element-129">
          <p className="text-[10px] text-slate-500 uppercase tracking-wider" data-id="element-130">
            Restantes
          </p>
          <p className="text-lg font-bold text-indigo-400" data-id="element-131">{remaining}</p>
        </div>
        <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50" data-id="element-132">
          <p className="text-[10px] text-slate-500 uppercase tracking-wider" data-id="element-133">
            Progreso
          </p>
          <p className="text-lg font-bold text-amber-400" data-id="element-134">{pctComplete}%</p>
        </div>
      </div>
    </div>;
}